// apps/web/src/lib/optimistic.ts
// Pure transforms over a TodayResponse, so a write can show up on screen
// before the server has confirmed it.
//
// Two rules keep this honest:
//
// 1. Totals are recomputed with `sumConsumed`/`sumWaterMl` — the same functions
//    the server uses to build the response. Nothing here re-derives arithmetic.
// 2. Nothing here touches `budget_kcal` or `burned_kcal`. Those come from
//    getDailyBudget, which folds in safety floors, calorie cycling and Health's
//    active energy; guessing at them locally would put a number on screen that
//    the server would then quietly disagree with. Writes that move the budget
//    (activities, weight) stay on the reload path.
//
// Every function returns a new object — callers keep the previous value as the
// rollback snapshot.
import type { Drink, FoodEntry, TodayResponse } from "@shared/types";
import { sumConsumed, sumWaterMl } from "@shared/totals";

/** Ids for rows that exist on screen but not yet in the database. Negative so
 *  they can never collide with a real AUTOINCREMENT id, and so an accidental
 *  `DELETE /api/food/-3` is a 404 rather than someone else's row. */
let nextPendingId = -1;
export function pendingId(): number {
  return nextPendingId--;
}

export function isPending(id: number): boolean {
  return id < 0;
}

function retotal(d: TodayResponse): TodayResponse {
  return {
    ...d,
    consumed: sumConsumed(d.entries, d.drinks),
    consumed_water_ml: sumWaterMl(d.drinks),
  };
}

export function withFoodAdded(d: TodayResponse, entry: FoodEntry): TodayResponse {
  return retotal({ ...d, entries: [...d.entries, entry] });
}

export function withFoodUpdated(
  d: TodayResponse,
  id: number,
  patch: Partial<FoodEntry>,
): TodayResponse {
  return retotal({
    ...d,
    entries: d.entries.map((e) => (e.id === id ? { ...e, ...patch } : e)),
  });
}

export function withFoodRemoved(d: TodayResponse, id: number): TodayResponse {
  return retotal({ ...d, entries: d.entries.filter((e) => e.id !== id) });
}

export function withDrinkAdded(d: TodayResponse, drink: Drink): TodayResponse {
  return retotal({ ...d, drinks: [...d.drinks, drink] });
}

export function withDrinkRemoved(d: TodayResponse, id: number): TodayResponse {
  return retotal({ ...d, drinks: d.drinks.filter((x) => x.id !== id) });
}
